/**
 * RiderSettlementHistory Component
 * 라이더 주정산내역 페이지 (중개사/협력사/본사용)
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {useRiderSettlementData} from '../hooks/useRiderSettlementData';
import {useDelayedLoading} from '@/shared/hooks/useDelayedLoading';
import {useUserInfo} from '@/shared/hooks/useCrudOperations';
import {usePagination} from '@/shared/hooks/usePagination';
import {useColumnSort} from '@/shared/hooks/useTableSort';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import {loadFilters, saveFilters} from '@/shared/utils/filterStorage';
import {
    getInitialDateRange,
    validateFilters
} from '../lib/riderSettlementHelpers';
import FilterSection from './FilterSection';
import SettlementTable from './SettlementTable';
import {RiderSettlement} from '../types/riderSettlement.types';

import '@/shared/styles/common-table.css';

/** 필터 저장 키 */
const FILTER_STORAGE_KEY = 'riderSettlementFilters';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = 10;

// ========================================
// Types
// ========================================
interface FilterState {
    acctStDate: string;
    acctEdDate: string;
    riderName: string;
    supplierId: string;
}

// ========================================
// Component
// ========================================
const RiderSettlementHistory: React.FC = () => {
    // ========================================
    // Hooks & Context
    // ========================================
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    // ========================================
    // 사용자 정보 파생 상태
    // ========================================
    const userInfo = getUserInfo();
    const userType = userInfo?.userType;
    const isHeadquartersUser = userType === 'HEADQUARTERS';
    const isSupplierUser = userType === 'SUPPLIER';
    const isRiderUser = userType === 'RIDER';
    const userSupplierId = isSupplierUser ? userInfo?.userId : null;
    const userRiderId = isRiderUser ? userInfo?.userId : null;

    // ========================================
    // 초기 데이터 로드 여부 결정
    // ========================================
    // 저장된 필터가 있으면 초기 조회 건너뛰기 (저장된 필터로 조회 예정)
    const skipInitialFetch = useMemo(() => {
        const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
        return !!(saved?.acctStDate && saved?.acctEdDate);
    }, []);

    // ========================================
    // 데이터 훅
    // ========================================
    const {
        settlements,
        suppliers,
        latestStartDate,
        latestEndDate,
        loading,
        refreshLatestDate,
        initialSettlementsFetched,
        refreshSettlements,
        downloadExcel
    } = useRiderSettlementData(userSupplierId, userRiderId, skipInitialFetch);

    const showLoading = useDelayedLoading(loading);

    // ========================================
    // 필터 상태
    // ========================================
    const [filters, setFilters] = useState<FilterState>(() => {
        // 협력사 사용자는 자신의 ID, 본사 사용자는 빈 문자열('')로 시작 (전체 의미)
        const initialSupplierId = isSupplierUser ? (userSupplierId || '') : '';
        
        const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
        return saved ? {
            acctStDate: saved.acctStDate || '',
            acctEdDate: saved.acctEdDate || '',
            riderName: saved.riderName || '',
            supplierId: initialSupplierId // 저장된 값 무시하고 항상 초기값 사용
        } : {
            acctStDate: '',
            acctEdDate: '',
            riderName: '',
            supplierId: initialSupplierId
        };
    });

    // ========================================
    // 다운로드 상태
    // ========================================
    const [downloading, setDownloading] = useState(false);

    // ========================================
    // 정렬 훅 사용 (컬럼 인덱스 기반)
    // ========================================
    const getValueByIndex = useCallback((settlement: RiderSettlement, colIndex: number): string | number => {
        switch (colIndex) {
            case 2: return settlement.riderId;
            case 3: return settlement.riderName;
            case 4: return settlement.riderType;
            case 5: return settlement.settlementStartDate;
            case 6: return settlement.settlementEndDate;
            case 7: return settlement.processCount;
            case 8: return settlement.deliveryFee;
            case 9: return settlement.additionalPayment;
            case 10: return settlement.brokerManageFee;
            case 11: return settlement.partnerOperationFee;
            case 12: return settlement.totalOperationFee;
            case 13: return settlement.taxableDeliveryFee;
            case 14: return settlement.monthlySalary;
            case 15: return settlement.riderEmployment;
            case 16: return settlement.riderIndustrial;
            case 17: return settlement.hourlyInsurance;
            case 18: return settlement.insuranceTotal;
            case 19: return settlement.promotion;
            case 20: return settlement.incomeTax;
            case 21: return settlement.localTax;
            case 22: return settlement.withholdingTax;
            case 23: return settlement.finalDeliveryFee;
            case 24: return settlement.advanceSettlement;
            case 25: return settlement.dailyDeduction;
            case 26: return settlement.actualPayment;
            default: return 0;
        }
    }, []);

    const {
        sortConfig,
        sortedData: sortedSettlements,
        handleSort,
        resetSort
    } = useColumnSort<RiderSettlement>({
        data: settlements,
        getValueByIndex
    });

    // ========================================
    // 페이지네이션 (정렬된 데이터 사용)
    // ========================================
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: sortedSettlements, itemsPerPage: ITEMS_PER_PAGE });

    // ========================================
    // Refs
    // ========================================
    const hasAppliedInitialFilter = useRef(false);

    // 초기 데이터 로드 완료 여부
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    // 최소 로딩 시간 경과 여부 (1.5초)
    const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false);
    const INITIAL_LOADING_DELAY = 1500;

    // ========================================
    // Effects
    // ========================================

    /**
     * 페이지 진입 시 최소 로딩 시간 타이머
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            setMinLoadingTimeElapsed(true);
        }, INITIAL_LOADING_DELAY);

        return () => clearTimeout(timer);
    }, []);

    /**
     * 초기 데이터 로드 완료 감지
     */
    useEffect(() => {
        // loading이 false가 되고 initialSettlementsFetched가 true이면 로드 완료로 처리
        if (!loading && (initialSettlementsFetched || latestStartDate !== undefined)) {
            setInitialDataLoaded(true);
        }
    }, [loading, initialSettlementsFetched, latestStartDate]);

    /**
     * 필터 변경 시 로컬 스토리지에 저장 (본사 사용자는 협력사 ID 제외)
     */
    useEffect(() => {
        const filtersToSave = {
            acctStDate: filters.acctStDate,
            acctEdDate: filters.acctEdDate,
            riderName: filters.riderName,
            supplierId: isSupplierUser ? filters.supplierId : '' // 본사 사용자는 협력사 ID 저장 안함
        };
        saveFilters(FILTER_STORAGE_KEY, filtersToSave);
    }, [filters, isSupplierUser]);

    /**
     * 초기 로딩 시 기본값 설정 및 저장된 필터로 조회
     */
    useEffect(() => {
        if (hasAppliedInitialFilter.current) return;

        if (latestStartDate && latestEndDate && !loading) {
            // 저장된 필터가 없으면 최근 정산시작일자와 종료일자로 설정
            if (!filters.acctStDate || !filters.acctEdDate) {
                const { startDate, endDate } = getInitialDateRange(latestStartDate, latestEndDate);
                const initialSupplierId = isSupplierUser ? (userSupplierId || '') : '';
                
                setFilters(prev => ({
                    ...prev,
                    acctStDate: startDate,
                    acctEdDate: endDate,
                    supplierId: initialSupplierId
                }));
            }

            // 저장된 필터가 있으면 해당 필터로 조회
            if (skipInitialFetch) {
                const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
                if (saved?.acctStDate && saved?.acctEdDate) {
                    refreshSettlements(
                        saved.acctStDate,
                        saved.acctEdDate,
                        saved.riderName || undefined,
                        saved.supplierId || undefined
                    ).then(() => {
                        setInitialDataLoaded(true);
                    });
                }
            }

            hasAppliedInitialFilter.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestStartDate, latestEndDate, loading, skipInitialFetch]);

    // ========================================
    // Handlers
    // ========================================

    /**
     * 필터 값 변경 핸들러
     */
    const handleFilterChange = useCallback((key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    /**
     * 검색 버튼 클릭 핸들러
     */
    const handleSearch = useCallback(async () => {
        const validation = validateFilters(
            filters.acctStDate,
            filters.acctEdDate,
            filters.supplierId,
            isRiderUser
        );
        if (!validation.isValid) {
            showToast(validation.message!, { type: ToastType.WARNING });
            return;
        }

        const resultCount = await refreshSettlements(
            filters.acctStDate,
            filters.acctEdDate,
            filters.riderName || undefined,
            filters.supplierId || undefined // 빈 문자열이면 undefined로 전달
        );
        resetPagination();

        // 조회 결과가 0건인 경우 Toast 메시지 표시
        if (resultCount === 0) {
            showToast('조회된 정산내역이 없습니다.', { type: ToastType.INFO });
        }
    }, [filters, isRiderUser, refreshSettlements, resetPagination, showToast]);

    /**
     * 초기화 버튼 클릭 핸들러
     * 페이지 진입 시 상태로 필터 및 정렬 초기화
     */
    const handleReset = useCallback(() => {
        // 기본 협력사 설정: 본사 사용자는 빈 문자열('') (전체), 협력사 사용자는 자신의 ID
        const initialSupplierId = isSupplierUser
            ? (userSupplierId || '')
            : '';

        // 필터 초기화
        setFilters({
            acctStDate: latestStartDate || '',
            acctEdDate: latestEndDate || '',
            riderName: '',
            supplierId: initialSupplierId
        });

        // 정렬 초기화
        resetSort();

        // 페이지네이션 초기화
        resetPagination();

        // 초기 상태로 데이터 조회
        if (latestStartDate && latestEndDate && (isRiderUser || initialSupplierId || !isSupplierUser)) {
            refreshSettlements(
                latestStartDate,
                latestEndDate,
                undefined,
                initialSupplierId || undefined
            );
        }
    }, [isSupplierUser, userSupplierId, latestStartDate, latestEndDate, isRiderUser, resetPagination, refreshSettlements, resetSort]);

    /**
     * 엑셀 다운로드 핸들러
     */
    const handleExcelDownload = useCallback(async () => {
        if (downloading) return;

        const validation = validateFilters(
            filters.acctStDate,
            filters.acctEdDate,
            filters.supplierId,
            isRiderUser
        );
        if (!validation.isValid) {
            showToast(validation.message!, { type: ToastType.WARNING });
            return;
        }

        setDownloading(true);
        try {
            await downloadExcel(
                filters.acctStDate,
                filters.acctEdDate,
                filters.riderName || undefined,
                filters.supplierId || undefined
            );
        } finally {
            setDownloading(false);
        }
    }, [filters, isRiderUser, downloading, downloadExcel, showToast]);

    // ========================================
    // Render - Loading States
    // ========================================
    // 초기 로딩: 최소 1.5초 타이머 완료 AND 데이터 로드 완료 시에만 로딩 해제
    const isInitialLoading = !minLoadingTimeElapsed || !initialDataLoaded;

    if (isInitialLoading || showLoading) {
        return <Loading message="라이더 주정산내역을 불러오는 중..." />;
    }

    // ========================================
    // Render - Main
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">라이더 주정산내역</h1>
            </div>

            {/* 검색 필터 영역 */}
            <FilterSection
                filters={filters}
                suppliers={suppliers}
                isRiderUser={isRiderUser}
                isSupplierUser={isSupplierUser}
                isHeadquartersUser={isHeadquartersUser}
                downloading={downloading}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onReset={handleReset}
                onExcelDownload={handleExcelDownload}
            />

            {/* 테이블 영역 */}
            <SettlementTable
                settlements={sortedSettlements}
                currentItems={currentItems}
                currentPage={currentPage}
                loading={loading}
                isRiderUser={isRiderUser}
                sortConfig={sortConfig}
                onSort={handleSort}
            />

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default RiderSettlementHistory;
